import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

import { useClientFlags } from '../hooks/useClientFlags'
import {
  applyDevFlagOverridesFromUrl,
  devFlagOverridesEnabled,
  readDevFlagOverrides,
  withDevFlagOverrides,
  type FlagOverrideValue,
} from './devFlagOverrides'

// RFC-0856 I10: in-house, OpenFeature-shaped flag context. Seeded from the
// GET /config/flags snapshot; consumers read via useBooleanFlagValue etc.
type FlagMap = Record<string, boolean | number | string>

export interface FlagsContextValue {
  flags: FlagMap
  isLoading: boolean
}

// Default is an empty snapshot so a component reading a flag WITHOUT the provider
// mounted (or in a test) safely falls back to its caller-supplied default rather
// than throwing.
// Exported so tests and the DEV preview harness can supply a fixed snapshot
// without the /config/flags fetch. Same raw-context escape hatch as
// authContextRaw / consentContextRaw.
export const FlagsContext = createContext<FlagsContextValue>({ flags: {}, isLoading: false })

export function FlagsProvider({ children }: { children: ReactNode }) {
  const { flags, isLoading } = useClientFlags()
  const [overrides, setOverrides] = useState<Record<string, FlagOverrideValue>>(() =>
    readDevFlagOverrides(),
  )

  // DEV-only: `?flags=…` writes overrides to localStorage. Read from
  // window.location rather than useSearchParams so this provider does not
  // require a Router ancestor — tests and the preview harness mount it bare.
  useEffect(() => {
    if (!devFlagOverridesEnabled()) return
    if (typeof window === 'undefined') return
    if (applyDevFlagOverridesFromUrl(window.location.search)) {
      setOverrides(readDevFlagOverrides())
    }
  }, [])

  const value = useMemo<FlagsContextValue>(
    // `withDevFlagOverrides` is the identity outside dev, and the whole branch
    // is statically removed from a production bundle.
    () => ({ flags: withDevFlagOverrides(flags, overrides), isLoading }),
    [flags, isLoading, overrides],
  )

  return (
    <FlagsContext.Provider value={value}>
      {children}
      <DevFlagOverrideBadge overrides={overrides} />
    </FlagsContext.Provider>
  )
}

// i18n-exempt: DEV-only diagnostic strings. This component returns null
// outside dev, and the whole branch is dropped from a production bundle, so
// these never reach a user and have no locale to belong to.
const BADGE_LABEL = 'flag overrides active'
const BADGE_HINT = '?flags=clear'

/**
 * Persistent reminder that flags are being faked.
 *
 * Overrides live in localStorage, so without this one outlives the session
 * that set it and the next person — or the same person tomorrow — debugs
 * behaviour that is not the real behaviour. Renders nothing outside dev, and
 * nothing when no override is set.
 */
function DevFlagOverrideBadge({ overrides }: { overrides: Record<string, FlagOverrideValue> }) {
  if (!devFlagOverridesEnabled()) return null
  const entries = Object.entries(overrides)
  if (entries.length === 0) return null
  return (
    <div
      data-testid="dev-flag-override-badge"
      className="pointer-events-none fixed bottom-2 left-2 z-[2000] rounded-md border border-amber-400/40 bg-amber-950/90 px-2 py-1 font-mono text-xs font-medium leading-4 text-amber-200 shadow-lg"
    >
      {/* i18n-exempt: DEV-only diagnostic, never present in a production bundle. */}
      <span className="font-semibold">{BADGE_LABEL}</span>
      {entries.map(([key, value]) => (
        <span key={key} className="ml-2">
          {key}={String(value)}
        </span>
      ))}
      <span className="ml-2 opacity-60">{BADGE_HINT}</span>
    </div>
  )
}

export function useFlagsContext(): FlagsContextValue {
  return useContext(FlagsContext)
}
